'use client';

import { c as _c } from "react/compiler-runtime";
import { FacebookButton, FacebookMessengerButton, WhatsappButton } from 'betterplace-react-components/transpile';
import classNames from 'classnames';
import { Alert, LayoutCluster, LayoutStack, LoadingSkeleton } from 'design-system/client';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import analytics, { type SharingOption } from 'product-analytics';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useNativeShareAvailable from '../hooks/useNativeShareAvailable';
import useShareImage, { ShareState } from '../hooks/useShareImage';
import InstagramShareButton from '../InstagramShareButton';
import styles from './SharingOptionsContent.module.css';
import CopyLinkButton from '@/fundraisingEventPage/_dependencies/components/FundraisingEventPageContent/CopyLinkButton';
import { Routes } from '@/helpers/utils';
const FADE_OUT_DELAY = 3000;
function LinkCopiedAlert() {
  const $ = _c(4);
  const t = useTranslations("nextjs.core");
  let t0;
  if ($[0] !== t) {
    t0 = t("copy_alert.headline");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <div className={styles.alertContainer}><Alert kind="success" icon={true}><p className={styles.alertText}>{t0}</p></Alert></div>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function InstagramCopiedAlert() {
  const $ = _c(6);
  const t = useTranslations("nextjs.core");
  let t0;
  if ($[0] !== t) {
    t0 = t("copy_alert.headline");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("copy_alert.text");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `${t0} ${t1}`;
  let t3;
  if ($[4] !== t2) {
    t3 = <div className={styles.alertContainer}><Alert kind="success" icon={true}><p className={styles.alertText}>{t2}</p></Alert></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function CopyErrorAlert() {
  const $ = _c(4);
  const t = useTranslations("nextjs.core");
  let t0;
  if ($[0] !== t) {
    t0 = t("copy_error_explanation");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <div className={styles.alertContainer}><Alert kind="error" icon={true}><p className={styles.alertText}>{t0}</p></Alert></div>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SharingOptionsContent({
  slug,
  utmMedium = '',
  utmCampaign = '',
  fundraisingEventId,
  title
}: {
  slug: string;
  fundraisingEventId: string;
  title: string;
  utmMedium?: string | string[];
  utmCampaign?: string | string[];
}) {
  const t = useTranslations('nextjs');
  const locale = useLocale();
  const shareUrl = useMemo(() => Routes.fundraisingEvents({
    slug,
    locale
  }), [locale, slug]);
  const [showLinkCopiedAlert, setShowLinkCopiedAlert] = useState(false);
  const [showInstagramCopiedAlert, setShowInstagramCopiedAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const {
    state,
    handleShare,
    previewUrl
  } = useShareImage(slug);
  const [shouldRenderImage, setShouldRenderImage] = useState(false);
  const [shouldRenderSkeletonImage, setShouldRenderSkeletonImage] = useState(false);
  const sharingClickedHandler = useCallback((sharing_option: SharingOption) => analytics.socialSharingClicked({
    id: fundraisingEventId,
    page_type: 'fundraising_event',
    title,
    icon_location: 'sharing_modal',
    sharing_option
  }), [fundraisingEventId, title]);
  useEffect(() => {
    if (state === ShareState.Ready) {
      setShowInstagramCopiedAlert(true);
      var instagramAlertTimeout = setTimeout(() => {
        setShowInstagramCopiedAlert(false);
      }, FADE_OUT_DELAY);
    }
    if (state === ShareState.Ready || state === ShareState.Success) {
      setShouldRenderSkeletonImage(false);
      setShouldRenderImage(true);
      return;
    }
    if (state === ShareState.Loading) {
      setShouldRenderSkeletonImage(true);
      return;
    }
    const transitionTimeout = setTimeout(() => {
      setShouldRenderImage(false);
      setShouldRenderSkeletonImage(false);
    }, 1000);
    if (state === ShareState.Error) {
      setShowErrorAlert(true);
      var errorAlertTimeout = setTimeout(() => {
        setShowErrorAlert(false);
      }, FADE_OUT_DELAY);
    }
    return () => {
      clearTimeout(transitionTimeout);
      clearTimeout(instagramAlertTimeout);
      clearTimeout(errorAlertTimeout);
    };
  }, [state]);
  const onLinkCopied = useCallback(() => {
    setShowLinkCopiedAlert(true);
    setTimeout(() => {
      setShowLinkCopiedAlert(false);
    }, FADE_OUT_DELAY);
    sharingClickedHandler('link_copy');
  }, [sharingClickedHandler]);
  const onCopyError = useCallback(() => {
    setShowErrorAlert(true);
    setTimeout(() => {
      setShowErrorAlert(false);
    }, FADE_OUT_DELAY);
  }, []);
  const isNativeShareAvailable = useNativeShareAvailable();
  const trackFacebookClicked = useCallback(() => sharingClickedHandler('facebook'), [sharingClickedHandler]);
  const trackWhatsappClicked = useCallback(() => sharingClickedHandler('whatsapp'), [sharingClickedHandler]);
  const trackMessengerClicked = useCallback(() => sharingClickedHandler('messenger'), [sharingClickedHandler]);
  const handleInstagramShareWithTracking = useCallback((shareUrl_0: string) => {
    sharingClickedHandler('instagram');
    void handleShare(shareUrl_0);
  }, [handleShare, sharingClickedHandler]);
  return <LayoutStack space="300" className={styles.contentContainer}>
      {t('receiver_pages.fundraising_event.help_to_collect_more_donations')}
      <div className={styles.sharingButtonsWrapper}>
        <LayoutCluster className={styles.sharingButtonsContainer} space="100" justify="start">
          <LayoutStack className={styles.socialSharingButtonContainer}>
            <FacebookButton shape="round" shareUrl={shareUrl} utmParams={{
            utm_campaign: utmCampaign,
            utm_medium: utmMedium,
            utm_source: 'Facebook'
          }} className={classNames(styles.socialSharingButton, styles.facebook)} beforeOnClick={trackFacebookClicked} />
            <div>{t('core.facebook')}</div>
          </LayoutStack>
          <LayoutStack className={styles.socialSharingButtonContainer}>
            <WhatsappButton shape="round" shareUrl={shareUrl} utmParams={{
            utm_campaign: utmCampaign,
            utm_medium: utmMedium,
            utm_source: 'WhatsApp'
          }} className={classNames(styles.socialSharingButton, styles.whatsapp)} beforeOnClick={trackWhatsappClicked} />
            <div>{t('core.whatsapp')}</div>
          </LayoutStack>
          <LayoutStack className={styles.socialSharingButtonContainer}>
            <FacebookMessengerButton shape="round" shareUrl={shareUrl} utmParams={{
            utm_campaign: utmCampaign,
            utm_medium: utmMedium,
            utm_source: 'FacebookMessenger'
          }} className={classNames(styles.socialSharingButton, styles.facebookMessenger)} facebookAppId={process.env.NEXT_PUBLIC_FB_APP_ID ?? ''} beforeOnClick={trackMessengerClicked} />
            <div>{t('core.facebook_messenger')}</div>
          </LayoutStack>
          <LayoutStack className={styles.socialSharingButtonContainer}>
            <CopyLinkButton shareUrl={shareUrl} utmParams={{
            utmCampaign,
            utmMedium
          }} onTextCopied={onLinkCopied} onError={onCopyError} fadeOutDelay={FADE_OUT_DELAY} />
            <div>{t('core.copy_link')}</div>
          </LayoutStack>
          {isNativeShareAvailable ? <LayoutStack className={styles.socialSharingButtonContainer}>
              <InstagramShareButton onClick={handleInstagramShareWithTracking} state={state} textCopied={state === ShareState.Ready} shareUrl={shareUrl} />
              <div>{t('core.instagram')}</div>
            </LayoutStack> : null}
        </LayoutCluster>
        <div className={classNames(styles.alertsWrapper, {
        [styles.open!]: showLinkCopiedAlert || showInstagramCopiedAlert || showErrorAlert,
        [styles.closed!]: !showLinkCopiedAlert && !showInstagramCopiedAlert && !showErrorAlert
      })}>
          {showLinkCopiedAlert && <LinkCopiedAlert />}
          {showInstagramCopiedAlert && <InstagramCopiedAlert />}
          {showErrorAlert && <CopyErrorAlert />}
        </div>
        <div className={classNames(styles.imageWrapper, {
        [styles.withImage!]: state === ShareState.Loading || state === ShareState.Ready
      })}>
          {shouldRenderSkeletonImage && <LoadingSkeleton height="711px" className={styles.imageSkeleton} />}
          {shouldRenderImage && previewUrl && <Image alt="" width={399} height={711} src={previewUrl} className={styles.previewImage} />}
        </div>
      </div>
    </LayoutStack>;
}
export default SharingOptionsContent;